import type { ChannelMember, ChannelMessage } from './queries';
import { getLocaleSnapshot, translate } from '@/lib/i18n/runtime';
import type { Locale } from '@/lib/i18n/locales';

const listFormatters = new Map<string, Intl.ListFormat>();
function formatNames(names: string[], locale: Locale): string {
  let formatter = listFormatters.get(locale);
  if (!formatter) {
    formatter = new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' });
    listFormatters.set(locale, formatter);
  }
  return formatter.format(names);
}
const copy = (key: string, params = {}, locale?: Locale) => translate(key, params, 'teamchat', locale);

export interface DockReadReceipt {
  status: 'sent' | 'read';
  label: string;
  title: string;
}

function validTimestamp(value: string | undefined): number | null {
  if (!value) return null;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
}

/**
 * Receipt for the newest canonical message authored by the current user.
 * A member has read it when their server-owned last_read_at reaches/passes
 * that message's created_at. Optimistic/failed rows deliberately do not count.
 */
export function latestOwnReadReceipt(
  messages: ChannelMessage[],
  members: ChannelMember[],
  currentUserId: string | undefined,
  isDirect: boolean,
  locale: Locale = getLocaleSnapshot().locale,
): { messageId: string; receipt: DockReadReceipt } | null {
  if (!currentUserId) return null;
  const message = [...messages]
    .reverse()
    .find(
      (item) =>
        item.user_id === currentUserId &&
        item.message_type !== 'system' &&
        !!item.id &&
        !item.pending &&
        !item.failed &&
        validTimestamp(item.created_at) !== null,
    );
  if (!message?.id) return null;

  const sentAt = validTimestamp(message.created_at);
  if (sentAt === null) return null;
  const readers = members.filter((member) => {
    if (!member.user_id || member.user_id === currentUserId) return false;
    const readAt = validTimestamp(member.last_read_at);
    return readAt !== null && readAt >= sentAt;
  });

  if (readers.length === 0) {
    return {
      messageId: message.id,
      receipt: { status: 'sent', label: copy('receipt.sent', {}, locale), title: copy('receipt.unread', {}, locale) },
    };
  }

  const names = readers.map((reader) => reader.user_name).filter((name): name is string => typeof name === 'string' && name !== '');
  const title = names.length > 0 ? copy('receipt.readBy', { names: formatNames(names, locale) }, locale) : copy('receipt.readMessage', {}, locale);
  const label = isDirect
    ? copy('receipt.read', {}, locale)
    : readers.length === 1 && names[0]
      ? copy('receipt.readBy', { names: names[0] }, locale)
      : copy('receipt.readCount', { count: readers.length }, locale);
  return { messageId: message.id, receipt: { status: 'read', label, title } };
}

export function typingLabel(names: string[]): string {
  return typingParts(names).map((part) => 'name' in part ? part.name : part.text).join('');
}

/** Split trusted catalog placeholders BEFORE inserting literal user names. */
export function typingParts(names: string[]): ({ text: string } | { name: string })[] {
  if (names.length === 0) return [];
  const key = names.length === 1 ? 'one' : names.length === 2 ? 'two' : 'more';
  return copy('typing.' + key, { first: '{first}', second: '{second}', count: names.length - 1 })
    .split(/(\{(?:first|second)\})/)
    .map((part) => part === '{first}' || part === '{second}'
      ? { name: names[part === '{first}' ? 0 : 1] }
      : { text: part });
}
